import React, { useState, useEffect, useRef } from "react"
import { Col, Row, UncontrolledAlert, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import { Link } from "react-router-dom"

const AddCompany = () => {
  const formRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [states, setStates] = useState([])
  const [districts, setDistricts] = useState([])
  const [taluks, setTaluks] = useState([])
  const [logo, setLogo] = useState(null)
  const [secondaryLogo, setSecondaryLogo] = useState(null)
  const [loadingDistricts, setLoadingDistricts] = useState(false)
  const [loadingTaluks, setLoadingTaluks] = useState(false)
  const [bankAccounts, setBankAccounts] = useState([{ bank_ifsc: "", bank_name: "", bank_branch: "", account_number: "", account_payee_name: "", account_type: "savings", linked_phone: "" }])

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get("states")
        setStates(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
      } catch (e) { console.error(e) }
    }
    load()
  }, [])

  const stateChangeHandler = async e => {
    const stateId = e.target.value; setDistricts([]); setTaluks([])
    if (!stateId) return; setLoadingDistricts(true)
    try { const resp = await api.get("districts/" + stateId); setDistricts(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || []) } catch (e) { console.error(e) }
    setLoadingDistricts(false)
  }

  const districtChangeHandler = async e => {
    const districtId = e.target.value; setTaluks([])
    if (!districtId) return; setLoadingTaluks(true)
    try { const resp = await api.get("taluks/" + districtId); setTaluks(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || []) } catch (e) { console.error(e) }
    setLoadingTaluks(false)
  }

  // Bank account helpers
  const addBankRow = () => setBankAccounts([...bankAccounts, { bank_ifsc: "", bank_name: "", bank_branch: "", account_number: "", account_payee_name: "", account_type: "savings", linked_phone: "" }])
  const removeBankRow = (i) => setBankAccounts(bankAccounts.filter((_, idx) => idx !== i))
  const updateBank = (i, field, val) => {
    const updated = [...bankAccounts]; updated[i][field] = val; setBankAccounts(updated)
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    try {
      const formData = new FormData()
      const allFields = [
        "name", "number", "state", "district", "taluk", "door", "village", "pincode",
        "gstIn", "billingTaxType", "cowMilkText", "buffaloMilkText", "agentCommission",
        "rateChartUpdates", "hideProducerRate", "hideBulkVendorsRate", "showProducerRate",
        "showBulkVendorsRate", "publishCCPurchases", "purchaseHistory", "incentiveChartUpdate",
        "unbilledAdvance", "manualEntries", "vlccCode",
        "hideIncentiveProducers", "hideIncentiveBulkVendors",
        "fullDeductionMaterialAdvances", "enableDeductionsBilling", "sameRateStockMaterials",
        "bankPayeeReference", "shortNumberReports", "mobileMandatoryProducer",
        "mobileMandatoryAgents", "customCowMilkText", "customBuffaloMilkText",
        "customMorningSession", "customEveningSession", "allocateVehicleTo",
        "billingValidateCCSession", "billingValidateVLCCSession"
      ]
      allFields.forEach(f => { if (values[f] !== undefined) formData.append(f, values[f]) })
      if (logo) formData.append("logo", logo)
      if (secondaryLogo) formData.append("secondaryLogo", secondaryLogo)

      const response = await api.post("create-company", formData, { headers: { "Content-Type": "multipart/form-data" } })
      if (response.data.success) {
        // Save bank accounts
        const companyId = response.data.id
        if (companyId && bankAccounts.some(a => a.account_number)) {
          await api.post("bulk-save-bank-accounts/" + companyId, { accounts: bankAccounts.filter(a => a.account_number) })
        }
        formRef.current.reset()
        setSubmitted(true)
        setLogo(null); setSecondaryLogo(null); setDistricts([]); setTaluks([])
        setBankAccounts([{ bank_ifsc: "", bank_name: "", bank_branch: "", account_number: "", account_payee_name: "", account_type: "savings", linked_phone: "" }])
        setTimeout(() => setSubmitted(false), 4000)
      }
    } catch (error) {
      console.error(error)
      alert(error.response?.data?.message || "Failed to create company")
    }
    setLoading(false)
  }

  // Reusable Yes/No radio
  const YesNo = ({ name, label, required }) => (
    <div className="mb-3">
      <AvRadioGroup inline name={name} label={label} required={required} errorMessage="Pick one!">
        <AvRadio label="Yes" value="yes" /><AvRadio label="No" value="no" />
      </AvRadioGroup>
    </div>
  )

  const SectionHeader = ({ icon, color, title }) => (
    <h6 style={{ color, borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "16px" }}>
      <i className={`fas ${icon}`}></i> {title}
    </h6>
  )

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Add Company</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <Link to="/companies" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link>
          </Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm ref={formRef} className="form-horizontal mt-4" onValidSubmit={handleValidSubmit}>

          {/* ─── Section 1: Company Information ─── */}
          <SectionHeader icon="fa-building" color="#6366f1" title="Company Information" />
          <Row>
            <Col md={6}><div className="mb-3"><AvField name="name" label="Company Name *" type="text" required placeholder="Enter Company Name" /></div></Col>
            <Col md={6}><div className="mb-3"><AvField name="number" label="Company Number *" type="text" required placeholder="Enter Company Number" /></div></Col>
          </Row>

          {/* ─── Section 2: Address ─── */}
          <SectionHeader icon="fa-map-marker-alt" color="#0ea5e9" title="Address Details" />
          <Row>
            <Col md={4}><div className="mb-3">
              <AvField type="select" name="state" label="State" onChange={stateChangeHandler}>
                <option value="">Select State</option>
                {states.map((s, i) => <option key={i} value={String(s.id)}>{s.state}</option>)}
              </AvField>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvField type="select" name="district" label={<>District {loadingDistricts && <Spinner size="sm" color="primary" className="ms-1" />}</>} onChange={districtChangeHandler}>
                <option value="">{loadingDistricts ? "Loading..." : districts.length ? "Select District" : "Select State first"}</option>
                {districts.map((d, i) => <option key={i} value={String(d.id)}>{d.district}</option>)}
              </AvField>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvField type="select" name="taluk" label={<>Taluk {loadingTaluks && <Spinner size="sm" color="primary" className="ms-1" />}</>}>
                <option value="">{loadingTaluks ? "Loading..." : taluks.length ? "Select Taluk" : "Select District first"}</option>
                {taluks.map((t, i) => <option key={i} value={t.taluk}>{t.taluk}</option>)}
              </AvField>
            </div></Col>
          </Row>
          <Row>
            <Col md={3}><div className="mb-3"><AvField name="door" label="Door No" type="text" placeholder="Door No" /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="village" label="Village Name" type="text" placeholder="Village Name" /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="pincode" label="Pincode" type="text" placeholder="e.g. 500033" /></div></Col>
            <Col md={3}><div className="mb-3"><AvField name="gstIn" label="Company GSTIN" type="text" placeholder="GSTIN Number" /></div></Col>
          </Row>

          {/* ─── Logos ─── */}
          <Row>
            <Col md={6}><div className="mb-3">
              <label className="form-label">Company Logo</label>
              <input type="file" className="form-control" accept="image/*" onChange={e => setLogo(e.target.files[0])} />
              <small className="text-muted">Image width not more than 200px</small>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <label className="form-label">Company Secondary Logo</label>
              <input type="file" className="form-control" accept="image/*" onChange={e => setSecondaryLogo(e.target.files[0])} />
            </div></Col>
          </Row>

          {/* ─── Section 3: Billing & Tax ─── */}
          <SectionHeader icon="fa-file-invoice" color="#22c55e" title="Billing & Tax" />
          <Row>
            <Col md={4}><div className="mb-3">
              <AvRadioGroup inline name="billingTaxType" label="Company Billing Tax Type *" required errorMessage="Pick one!">
                <AvRadio label="No Tax" value="no tax" /><AvRadio label="GST" value="gst" />
              </AvRadioGroup>
            </div></Col>
            <Col md={4}><div className="mb-3"><AvField name="cowMilkText" label="Cow Milk Text for API" type="text" value="CGM" placeholder="e.g. CGM" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="buffaloMilkText" label="Buffalo Milk Text for API" type="text" value="BGM" placeholder="e.g. BGM" /></div></Col>
          </Row>

          {/* ─── Section 4: App & Rate Settings ─── */}
          <SectionHeader icon="fa-cogs" color="#f59e0b" title="App & Rate Settings" />
          <Row>
            <Col md={6}><YesNo name="agentCommission" label="Agent Commission based on VLCC Quantity *" required /></Col>
            <Col md={6}><YesNo name="rateChartUpdates" label="Enable Approval for Rate Chart Updates *" required /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="hideProducerRate" label="Hide Rate for Producers in Farmer App" /></Col>
            <Col md={6}><YesNo name="hideBulkVendorsRate" label="Hide Rate for Bulk Vendors in Farmer App" /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="showProducerRate" label="Show Net Rate for Producers in Farmer App" /></Col>
            <Col md={6}><YesNo name="showBulkVendorsRate" label="Show Net Rate for Bulk Vendors in Farmer App" /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="hideIncentiveProducers" label="Hide Incentive for Producers in Farmer App *" required /></Col>
            <Col md={6}><YesNo name="hideIncentiveBulkVendors" label="Hide Incentive for Bulk Vendors in Farmer App *" required /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="publishCCPurchases" label="Manually Publish CC Purchases to Farmer App for Bulk Vendors" /></Col>
            <Col md={6}><div className="mb-3"><AvField name="purchaseHistory" label="Number of days of purchase history in Farmer App" type="number" value="365" placeholder="e.g. 365" /></div></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="incentiveChartUpdate" label="Enable Approval for Incentive Chart Updates" /></Col>
            <Col md={6}><YesNo name="unbilledAdvance" label="Unbilled Advance Available" /></Col>
          </Row>

          {/* ─── Section 5: Advanced Settings ─── */}
          <SectionHeader icon="fa-sliders-h" color="#8b5cf6" title="Advanced Settings" />
          <Row>
            <Col md={6}><YesNo name="fullDeductionMaterialAdvances" label="Always add full deduction for Material Advances *" required /></Col>
            <Col md={6}><YesNo name="enableDeductionsBilling" label="Enable deductions for Producer Billing Vendors by Commission *" required /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="sameRateStockMaterials" label="Apply same rate for Stock Materials in CC and VLCC *" required /></Col>
            <Col md={6}><YesNo name="bankPayeeReference" label="Company Bank Payee Reference is required *" required /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="manualEntries" label="Highlight Edited and Manual Entries" /></Col>
            <Col md={6}><YesNo name="vlccCode" label="Enable Milktypewise Code for VLCC *" required /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="shortNumberReports" label="Use Producer Short Number in Listing and Reports" /></Col>
            <Col md={6}><YesNo name="mobileMandatoryProducer" label="Mobile Number is Mandatory for Producer *" required /></Col>
          </Row>
          <Row>
            <Col md={6}><YesNo name="mobileMandatoryAgents" label="Mobile Number is Mandatory for Agents/Vendors *" required /></Col>
          </Row>

          {/* ─── Section 6: Custom Labels ─── */}
          <SectionHeader icon="fa-tags" color="#ec4899" title="Custom Labels & Session" />
          <Row>
            <Col md={6}><div className="mb-3">
              <AvField name="customCowMilkText" label="Custom Milk Type Text for Cow Milk" type="text" value="CM" placeholder="e.g. CM" />
              <small className="text-muted">Displayed in Procurement Listing, Reports, Bill Printouts, etc</small>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvField name="customBuffaloMilkText" label="Custom Milk Type Text for Buffalo Milk" type="text" value="BM" placeholder="e.g. BM" />
              <small className="text-muted">Displayed in Procurement Listing, Reports, Bill Printouts, etc</small>
            </div></Col>
          </Row>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvField name="customMorningSession" label="Custom Session Text for Morning Session" type="text" placeholder="e.g. Morning" />
              <small className="text-muted">Displayed in Procurement Listing, Reports, Bill Printouts, etc</small>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvField name="customEveningSession" label="Custom Session Text for Evening Session" type="text" placeholder="e.g. Evening" />
              <small className="text-muted">Displayed in Procurement Listing, Reports, Bill Printouts, etc</small>
            </div></Col>
          </Row>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="allocateVehicleTo" label="Allocate Pickup Vehicle To *" required errorMessage="Pick one!">
                <AvRadio label="Routes" value="1" /><AvRadio label="Centers/Vendors" value="2" /><AvRadio label="Manually Select" value="3" />
              </AvRadioGroup>
            </div></Col>
          </Row>

          {/* ─── Section 7: Billing Validations ─── */}
          <SectionHeader icon="fa-check-double" color="#ef4444" title="Billing Validations" />
          <Row>
            <Col md={6}><YesNo name="billingValidateCCSession" label="Billing Validate CC Session Close *" required /></Col>
            <Col md={6}><YesNo name="billingValidateVLCCSession" label="Billing Validate VLCC Session Close *" required /></Col>
          </Row>

          {/* ─── Section 8: Bank Accounts ─── */}
          <SectionHeader icon="fa-university" color="#0d9488" title="Client Company Bank Accounts Information" />
          <div className="table-responsive mb-3">
            <table className="table table-bordered" style={{ fontSize: "13px" }}>
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th>Bank IFSC</th><th>Bank Name</th><th>Bank Branch</th>
                  <th>Account Number *</th><th>Account Payee Name *</th>
                  <th>Account Type *</th><th>Linked Phone</th><th style={{ width: "50px" }}></th>
                </tr>
              </thead>
              <tbody>
                {bankAccounts.map((acc, i) => (
                  <tr key={i}>
                    <td><input className="form-control form-control-sm" value={acc.bank_ifsc} onChange={e => updateBank(i, "bank_ifsc", e.target.value)} placeholder="IFSC" /></td>
                    <td><input className="form-control form-control-sm" value={acc.bank_name} onChange={e => updateBank(i, "bank_name", e.target.value)} placeholder="Bank Name" /></td>
                    <td><input className="form-control form-control-sm" value={acc.bank_branch} onChange={e => updateBank(i, "bank_branch", e.target.value)} placeholder="Branch" /></td>
                    <td><input className="form-control form-control-sm" value={acc.account_number} onChange={e => updateBank(i, "account_number", e.target.value)} placeholder="Account No" required /></td>
                    <td><input className="form-control form-control-sm" value={acc.account_payee_name} onChange={e => updateBank(i, "account_payee_name", e.target.value)} placeholder="Payee Name" required /></td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "3px" }}>
                          <input type="radio" checked={acc.account_type === "savings"} onChange={() => updateBank(i, "account_type", "savings")} /> Savings
                        </label>
                        <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "3px" }}>
                          <input type="radio" checked={acc.account_type === "current"} onChange={() => updateBank(i, "account_type", "current")} /> Current
                        </label>
                      </div>
                    </td>
                    <td><input className="form-control form-control-sm" value={acc.linked_phone} onChange={e => updateBank(i, "linked_phone", e.target.value)} placeholder="Phone" /></td>
                    <td>
                      {bankAccounts.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeBankRow(i)} title="Remove">
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addBankRow}>
              <i className="fas fa-plus me-1"></i> Add Bank Account
            </button>
          </div>

          {/* Submit */}
          <div className="mb-3 row mt-4">
            <div className="col-6">
              {submitted && <UncontrolledAlert color="success"><strong>Company Added Successfully!</strong></UncontrolledAlert>}
            </div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner size="sm" /><span style={{ paddingLeft: "10px" }}>Adding...</span></> : <span><i className="fas fa-plus-circle"></i> Add Company</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default AddCompany
